import React, { useState } from "react"
import { Link } from "react-router-dom"
import { Phone, RefreshCw, ExternalLink, Sparkles, ArrowRight, MapPin } from "lucide-react"

import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "src/components/ui/sheet"
import { Button } from "src/components/ui/button"
import { Skeleton } from "src/components/ui/skeleton"
import { Separator } from "src/components/ui/separator"
import { cn } from "src/utils/cn"
import { ScheduleVisitModal } from "src/features/site_visits/components/ScheduleVisitModal"

import { PairBadge } from "./PairBadge"
import { PairBreakdown } from "./PairBars"
import { VERDICT_LABELS } from "../constants"

// Bands where booking a site visit is a sensible next action.
const BOOKABLE_BANDS = ["hot", "warm"]

// Side panel showing a single lead's full PAIR assessment: composite, verdict,
// reasons, next action, the per-dimension breakdown with evidence, and the full
// WhatsApp transcript the engine had with the lead.
export function QualificationDetailSheet({ open, onOpenChange, detail, loading, onRescore, onRescorePending, onScheduled }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col p-0 sm:max-w-xl">
        {loading || !detail ? (
          <LoadingState />
        ) : (
          <Loaded detail={detail} onRescore={onRescore} onRescorePending={onRescorePending} onScheduled={onScheduled} />
        )}
      </SheetContent>
    </Sheet>
  )
}

function Loaded({ detail, onRescore, onRescorePending, onScheduled }) {
  const composite = detail.composite ?? 0
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const canBook = BOOKABLE_BANDS.includes(detail.band)

  // Shape the PAIR detail into the lead object ScheduleVisitModal expects
  // (id + name + a {id, name} project it can pre-select).
  const modalLead = {
    id: detail.id,
    name: detail.name,
    project: detail.project_ref || null,
  }

  return (
    <>
      <SheetHeader>
        {/* pr-10 keeps the title/description clear of the absolute close (X) button. */}
        <div className="flex items-start justify-between gap-3 pr-10">
          <div className="min-w-0">
            <SheetTitle className="flex items-center gap-2 text-lg">
              <span className="truncate">{detail.name}</span>
              <PairBadge band={detail.band} label={detail.band_label} />
            </SheetTitle>
            <SheetDescription className="mt-0.5 truncate">
              {detail.project || "No project"} · {detail.phone || "no phone"}
            </SheetDescription>
          </div>
          <Link to={`/leads/${detail.id}`} className="shrink-0">
            <Button variant="outline" size="sm">
              Open lead <ExternalLink className="ml-1 h-3.5 w-3.5" />
            </Button>
          </Link>
        </div>
      </SheetHeader>

      {/* Scrollable body — its own horizontal padding so content lines up with
          the header/footer instead of running to the panel edge. */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {/* Composite + verdict hero */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">PAIR composite</p>
              <p className="text-3xl font-bold tabular-nums text-slate-900">
                {composite}<span className="text-base font-normal text-slate-400">/100</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Verdict</p>
              <p className="text-sm font-semibold text-slate-700">
                {VERDICT_LABELS[detail.verdict] || detail.verdict || "—"}
              </p>
            </div>
          </div>
        </div>

        {/* Next action + reasons */}
        {detail.next_action && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
            <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500">Recommended next step</p>
              <p className="text-sm text-slate-700">{detail.next_action}</p>
            </div>
          </div>
        )}

        {Array.isArray(detail.reasons) && detail.reasons.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {detail.reasons.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                {r}
              </li>
            ))}
          </ul>
        )}

        <Separator className="my-5" />

        <h3 className="mb-3 text-sm font-semibold text-slate-800">PAIR breakdown</h3>
        <PairBreakdown dimensions={detail.dimensions} />

        <Separator className="my-5" />

        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">Conversation</h3>
          <span className="text-[11px] text-slate-400">
            {detail.conversation?.turn_count ? `${detail.conversation.turn_count} turns` : ""}
          </span>
        </div>
        <Transcript messages={detail.transcript || []} />
      </div>

      <SheetFooter>
        {detail.phone && (
          <a href={`tel:${detail.phone}`} className="flex-1">
            <Button className="w-full" variant="default">
              <Phone className="mr-1.5 h-4 w-4" /> Call now
            </Button>
          </a>
        )}
        {canBook && (
          <Button variant="outline" className="flex-1" onClick={() => setScheduleOpen(true)}>
            <MapPin className="mr-1.5 h-4 w-4" /> Book visit
          </Button>
        )}
        <Button variant="outline" size="icon" title="Re-score" onClick={() => onRescore?.(detail.id)} disabled={onRescorePending}>
          <RefreshCw className={cn("h-4 w-4", onRescorePending && "animate-spin")} />
        </Button>
      </SheetFooter>

      {/* Reuse the app-wide site-visit scheduler, pre-filled with this lead. */}
      <ScheduleVisitModal
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        lead={modalLead}
        onScheduled={() => {
          setScheduleOpen(false)
          onScheduled?.(detail)
        }}
      />
    </>
  )
}

function Transcript({ messages }) {
  if (!messages.length) {
    return <p className="text-sm text-slate-400">No messages yet.</p>
  }
  return (
    <div className="space-y-2">
      {messages.map((m, i) => {
        const outbound = m.direction === "outbound"
        return (
          <div key={i} className={cn("flex", outbound ? "justify-end" : "justify-start")}>
            <div
              className={cn(
                "max-w-[80%] rounded-2xl px-3 py-2 text-[13px]",
                outbound
                  ? "rounded-br-sm bg-indigo-600 text-white"
                  : "rounded-bl-sm bg-slate-100 text-slate-800"
              )}
            >
              {m.text}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex-1 space-y-4 px-6 py-6">
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  )
}
